import { ClassifiedsInlineBannerCarousel } from '@/components/classifieds-inline-banner-carousel';
import { MostSearchedCategories } from '@/components/most-searched-categories';
import { SearchAutocomplete } from '@/components/search-autocomplete';
import { SearchTypeTabs } from '@/components/search/search-type-tabs';
import { SmartSearchForm } from '@/components/search/smart-search-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { Input } from '@/components/ui/input';
import { useBannerTracking, useBannerVisibilityTracker } from '@/hooks/use-banner-tracking';
import { RecaptchaProvider, useRecaptchaToken } from '@/hooks/use-recaptcha';
import FrontendLayout from '@/layouts/frontend-layout';
import { apiClient } from '@/lib/apiClient';
import { getPublicPriceLabel } from '@/lib/currency';
import { toJsonLd } from '@/lib/json-ld';
import { getMediaUrl } from '@/lib/media';
import { DEFAULT_SMART_SEARCH_SETTINGS, trackSearchEvent } from '@/lib/smart-search';
import { cn } from '@/lib/utils';
import { login, search } from '@/routes';
import { type Category, type Classified, type MediaItem, type SharedData } from '@/types';
import { type SearchType } from '@/types/search-criteria';
import { Head, Link, router, usePage } from '@inertiajs/react';
import axios from 'axios';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronDown, Facebook, Instagram, Linkedin, Search, Twitter, Youtube } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Banner {
    id: number;
    title: string;
    link_url: string | null;
    section: string;
    is_active: boolean;
    description: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
    views_count: number;
    clicks_count: number;
}

interface BlogPost {
    id: number;
    url: string;
    titulo: string;
    resumo: string;
    imagem: string;
    autor: { id: number; nome: string };
    categoria: { id: number; nome: string; slug: string };
}

interface WelcomeProps extends SharedData {
    categories: Category[];
    featured: Classified[];
    banners: Banner[];
    sponsorBanner: Banner | null;
    featuredBanner: Banner | null;
    recentlyViewedBanner: Banner | null;
    newsBanner: Banner | null;
    recentlyViewed: Classified[];
    blogPosts: BlogPost[];
}

function WelcomeContent() {
    const executeRecaptcha = useRecaptchaToken();
    const {
        auth,
        categories,
        featured,
        banners,
        sponsorBanner,
        featuredBanner,
        recentlyViewedBanner,
        newsBanner,
        recentlyViewed,
        appUrl,
        blogPosts,
        smartSearch: smartSearchSettings = DEFAULT_SMART_SEARCH_SETTINGS,
    } = usePage<WelcomeProps>().props;
    const [activeCondition, setActiveCondition] = useState<'all' | 'new' | 'used'>('all');
    // CN-200 (5.4): "Embarcações | Empresas" tabs of the search card.
    const [homeSearchType, setHomeSearchType] = useState<SearchType>('embarcacoes');
    const boatsSmartSearchEnabled = smartSearchSettings.enabled.embarcacoes;

    const handleHomeSearchTypeChange = (next: SearchType) => {
        if (next !== homeSearchType) {
            trackSearchEvent('busca_aba_trocada', next, { origem: 'home' });
        }

        setHomeSearchType(next);
    };
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterLoading, setNewsletterLoading] = useState(false);
    const [newsletterMessage, setNewsletterMessage] = useState('');
    const [newsletterStatus, setNewsletterStatus] = useState<'success' | 'error' | null>(null);
    const [emailSent, setEmailSent] = useState(false);

    // Search State
    const [mainSearchQuery, setMainSearchQuery] = useState('');

    // Advanced Search State
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [size, setSize] = useState('');
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [advancedSearchError, setAdvancedSearchError] = useState('');
    const [favoriteStates, setFavoriteStates] = useState<Record<number, boolean>>({});

    const plugin = useRef(Autoplay({ delay: 10000, stopOnInteraction: true }));

    // Format price input as BRL currency
    const formatPriceInput = (value: string): string => {
        // Remove all non-numeric characters
        const numericValue = value.replace(/\D/g, '');

        if (!numericValue) return '';

        // Convert to number and divide by 100 to get decimal places
        const numberValue = parseInt(numericValue) / 100;

        // Format as BRL currency
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numberValue);
    };

    // Handle max price input change with formatting
    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatPriceInput(e.target.value);
        setMaxPrice(formattedValue);
        setAdvancedSearchError('');
    };

    // Handle min price input change with formatting
    const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatPriceInput(e.target.value);
        setMinPrice(formattedValue);
        setAdvancedSearchError('');
    };

    // Format size input (float with "pés" suffix)
    const formatSizeInput = (value: string): string => {
        // Remove everything except numbers, comma and dot
        let numericValue = value.replace(/[^\d,.]/g, '');

        // Replace comma with dot for standardization
        numericValue = numericValue.replace(',', '.');

        // Ensure only one decimal point
        const parts = numericValue.split('.');
        if (parts.length > 2) {
            numericValue = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limit to 2 decimal places
        if (parts.length === 2 && parts[1].length > 2) {
            numericValue = parts[0] + '.' + parts[1].substring(0, 2);
        }

        if (!numericValue) return '';

        return numericValue;
    };

    // Handle size input change with validation
    const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatSizeInput(e.target.value);

        // Validate if it's a positive number
        if (formattedValue === '' || parseFloat(formattedValue) >= 0) {
            setSize(formattedValue);
            setAdvancedSearchError('');
        }
    };

    const { trackBannerView, handleBannerClick } = useBannerTracking();
    const [heroCarouselApi, setHeroCarouselApi] = useState<CarouselApi | null>(null);

    // Prepare hero images from banners or fallback to static images
    const heroImages = useMemo(
        () =>
            banners && banners.length > 0
                ? banners.map((banner) => ({
                      id: banner.id,
                      src: getMediaUrl(banner.desktop_image, 'banner_desktop', '/boat-banner-1.png'),
                      srcMobile: getMediaUrl(banner.mobile_image, 'banner_mobile', '/boat-banner-1-mobile.png'),
                      alt: banner.image_alt || banner.title,
                      link_url: banner.link_url,
                  }))
                : [
                      {
                          id: null,
                          src: '/boat-banner-1.png',
                          srcMobile: '/boat-banner-1-mobile.png',
                          alt: 'Nautical banner 1',
                          link_url: null,
                      },
                      {
                          id: null,
                          src: '/boat-banner-2.png',
                          srcMobile: '/boat-banner-2-mobile.png',
                          alt: 'Nautical banner 2',
                          link_url: null,
                      },
                  ],
        [banners],
    );

    // Track view when active hero slide changes
    useEffect(() => {
        if (!heroCarouselApi) {
            return;
        }

        const trackCurrentHero = () => {
            const selectedIndex = heroCarouselApi.selectedScrollSnap();
            const selectedBanner = heroImages[selectedIndex];

            if (selectedBanner?.id) {
                trackBannerView(selectedBanner.id, 'welcome-hero');
            }
        };

        trackCurrentHero();
        heroCarouselApi.on('select', trackCurrentHero);
        heroCarouselApi.on('reInit', trackCurrentHero);

        return () => {
            heroCarouselApi.off('select', trackCurrentHero);
            heroCarouselApi.off('reInit', trackCurrentHero);
        };
    }, [heroCarouselApi, heroImages, trackBannerView]);

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setNewsletterLoading(true);
        setNewsletterMessage('');
        setNewsletterStatus(null);

        try {
            let recaptchaToken = '';
            if (executeRecaptcha) {
                recaptchaToken = await executeRecaptcha('newsletter_subscribe');
            }

            const response = await apiClient.post('/newsletter/subscribe', {
                email: newsletterEmail,
                recaptcha_token: recaptchaToken,
            });

            if (response.data.success) {
                setNewsletterEmail('');
                setNewsletterMessage('');
                setNewsletterStatus('success');
                setEmailSent(true);
            }
        } catch (error: unknown) {
            setNewsletterStatus('error');
            if (axios.isAxiosError(error) && error.response?.data?.errors) {
                const errs = error.response.data.errors;
                setNewsletterMessage(errs.email?.[0] || errs.recaptcha_token?.[0] || 'Erro ao inscrever. Tente novamente.');
            } else {
                setNewsletterMessage('Erro ao inscrever. Tente novamente.');
            }
        } finally {
            setNewsletterLoading(false);
        }
    };

    // Auto-favorite support after login for classifieds on home page.
    // If user was redirected back with auto_favorite and favorite_id flags,
    // and that classified exists on this page and is not yet favorited in local state,
    // toggle it once and then clean the URL.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!auth?.user) return;

        try {
            const url = new URL(window.location.href);
            const autoFavorite = url.searchParams.get('auto_favorite');
            const favoriteIdParam = url.searchParams.get('favorite_id');

            if (autoFavorite !== '1' || !favoriteIdParam) {
                return;
            }

            const favoriteId = Number(favoriteIdParam);
            if (!favoriteId || Number.isNaN(favoriteId)) {
                url.searchParams.delete('auto_favorite');
                url.searchParams.delete('favorite_id');
                window.history.replaceState(null, '', url.toString());
                return;
            }

            // Check if this classified is present in featured or recently viewed
            const allClassifieds = [...(featured || []), ...(recentlyViewed || [])];
            const classifiedExists = allClassifieds.some((c) => c.id === favoriteId);

            if (!classifiedExists) {
                url.searchParams.delete('auto_favorite');
                url.searchParams.delete('favorite_id');
                window.history.replaceState(null, '', url.toString());
                return;
            }

            // Avoid toggling if local state already considers it favorited
            if (favoriteStates[favoriteId]) {
                url.searchParams.delete('auto_favorite');
                url.searchParams.delete('favorite_id');
                window.history.replaceState(null, '', url.toString());
                return;
            }

            apiClient
                .post(`/api/favorites/${favoriteId}/toggle`)
                .then((response) => {
                    setFavoriteStates((prev) => ({
                        ...prev,
                        [favoriteId]: response.data.is_favorited,
                    }));
                })
                .catch((error) => {
                    console.error('Error auto-toggling favorite after login on home page:', error);
                })
                .finally(() => {
                    url.searchParams.delete('auto_favorite');
                    url.searchParams.delete('favorite_id');
                    window.history.replaceState(null, '', url.toString());
                });
        } catch (error) {
            console.error('Error handling auto_favorite params on home page:', error);
        }
    }, [auth?.user, favoriteStates, featured, recentlyViewed]);

    const redirectToLoginForFavorite = (boatId: number) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('auto_favorite', '1');
        currentUrl.searchParams.set('favorite_id', String(boatId));

        router.visit(login().url, {
            data: { return_url: currentUrl.toString() },
        });
    };

    const hasHttpStatus = (error: unknown, status: number) => {
        if (typeof error !== 'object' || error === null || !('response' in error)) {
            return false;
        }

        const response = (error as { response?: { status?: number } }).response;
        return response?.status === status;
    };

    const handleFavorite = async (boatId: number) => {
        // Check if user is logged in
        if (!auth?.user) {
            redirectToLoginForFavorite(boatId);
            return;
        }

        try {
            const response = await apiClient.post<{ is_favorited: boolean }>(`/api/favorites/${boatId}/toggle`);

            // Update local state
            setFavoriteStates((prev) => ({
                ...prev,
                [boatId]: response.data.is_favorited,
            }));
        } catch (error: unknown) {
            if (hasHttpStatus(error, 401)) {
                redirectToLoginForFavorite(boatId);
            } else {
                console.error('Error toggling favorite:', error);
            }
        }
    };

    const handleAdvancedSearch = () => {
        setAdvancedSearchError('');

        // Validate price range: min price should not be greater than max price
        let minPriceValue: number | null = null;
        let maxPriceValue: number | null = null;

        if (minPrice.trim()) {
            const priceValue = minPrice.replace(/[^\d,]/g, '').replace(',', '.');
            const numericPrice = parseFloat(priceValue);
            if (!isNaN(numericPrice)) {
                minPriceValue = numericPrice;
            }
        }

        if (maxPrice.trim()) {
            const priceValue = maxPrice.replace(/[^\d,]/g, '').replace(',', '.');
            const numericPrice = parseFloat(priceValue);
            if (!isNaN(numericPrice)) {
                maxPriceValue = numericPrice;
            }
        }

        // Check if min price is greater than max price
        if (minPriceValue !== null && maxPriceValue !== null && minPriceValue > maxPriceValue) {
            setAdvancedSearchError('O valor mínimo não pode ser maior que o valor máximo.');
            return;
        }

        // Build query parameters object
        const params: Record<string, string | number> = {};

        if (mainSearchQuery.trim()) {
            params.pesquisa = mainSearchQuery.trim();
        }

        // Add condition filter if not 'all'
        if (activeCondition !== 'all') {
            params.condition = activeCondition;
        }

        // Add min price if provided
        if (minPriceValue !== null) {
            params.price_min = minPriceValue;
        }

        // Add max price if provided
        if (maxPriceValue !== null) {
            params.price_max = maxPriceValue;
        }

        // Add size if provided (convert to number)
        if (size.trim()) {
            const numericSize = parseFloat(size);
            if (!isNaN(numericSize) && numericSize > 0) {
                params.size = numericSize;
            }
        }

        // Navigate to search results page
        router.get(search().url, params);
    };

    // WebSite schema for SEO
    const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${appUrl}/#website`,
        name: 'Compre Náutica',
        url: appUrl,
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${appUrl}/search?search={search_term_string}&sort=relevance&type=embarcacoes`,
            },
            'query-input': 'required name=search_term_string',
        },
    };

    const getConditionPillClass = (condition: 'all' | 'new' | 'used') =>
        cn(
            'rounded-full px-4 py-1 text-sm',
            activeCondition === condition
                ? 'bg-blue-800 text-white hover:bg-blue-800 hover:text-white'
                : 'bg-blue-50 text-blue-800 hover:bg-gray-200 hover:text-gray-700',
            condition === 'all' && 'font-medium',
        );

    const sponsorMobileImage = getMediaUrl(sponsorBanner?.mobile_image, 'banner_mobile');
    const sponsorDesktopImage = getMediaUrl(sponsorBanner?.desktop_image, 'banner_desktop');
    const sponsorImageSrc = sponsorDesktopImage || sponsorMobileImage;
    const newsBannerImage = getMediaUrl(
        newsBanner?.desktop_image,
        'banner_desktop',
        getMediaUrl(newsBanner?.mobile_image, 'banner_mobile', '/ad-1.png'),
    );
    const sponsorBannerRef = useBannerVisibilityTracker<HTMLDivElement>(sponsorBanner?.id, 'welcome-sponsor-banner', trackBannerView);
    const newsBannerRef = useBannerVisibilityTracker<HTMLDivElement>(newsBanner?.id, 'welcome-news-banner', trackBannerView);

    return (
        <FrontendLayout>
            <Head title="Compre Náutica | Embarcações à venda, empresas e serviços náuticos">
                <meta
                    name="description"
                    content="Compre Náutica é o marketplace náutico para comprar e vender embarcações, encontrar empresas e serviços náuticos em todo o Brasil."
                />
                <link rel="canonical" href={`${appUrl}/`} />
                <meta name="robots" content="index,follow" />

                {/* Open Graph */}
                <meta property="og:title" content="Compre Náutica" />
                <meta property="og:description" content="Marketplace de embarcações, empresas e serviços náuticos." />
                <meta property="og:image" content={`${appUrl}/cn_logo_v2.png`} />
                <meta property="og:url" content={`${appUrl}/`} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="pt_BR" />

                {/* JSON-LD: WebSite + SearchAction */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(websiteSchema) }} />
            </Head>

            <div className="w-full bg-gray-100">
                {/* Hero Section */}
                <section className="relative h-[199px] overflow-hidden bg-transparent text-white md:aspect-[3/1] md:h-auto">
                    <div className="absolute inset-0">
                        <Carousel
                            opts={{
                                align: 'start',
                                loop: true,
                            }}
                            plugins={[plugin.current]}
                            setApi={setHeroCarouselApi}
                            className="h-full w-full"
                        >
                            <CarouselContent className="ml-0 h-full">
                                {heroImages.map((image, index) => (
                                    <CarouselItem key={index} className="min-w-0 basis-full pl-0">
                                        {image.link_url ? (
                                            <a
                                                href={image.link_url}
                                                onClick={(e) => handleBannerClick(e, image.id, image.link_url)}
                                                className="relative block h-[199px] w-full cursor-pointer md:h-full"
                                                aria-label={image.alt}
                                            >
                                                <picture>
                                                    <source media="(max-width: 767px)" srcSet={image.srcMobile || image.src} />
                                                    <img
                                                        src={image.src}
                                                        alt={image.alt}
                                                        loading={index === 0 ? 'eager' : 'lazy'}
                                                        fetchPriority={index === 0 ? 'high' : 'auto'}
                                                        decoding="async"
                                                        className="h-full w-full object-cover object-center"
                                                    />
                                                </picture>
                                            </a>
                                        ) : (
                                            <div className="relative h-[199px] w-full md:h-full">
                                                <picture>
                                                    <source media="(max-width: 767px)" srcSet={image.srcMobile || image.src} />
                                                    <img
                                                        src={image.src}
                                                        alt={image.alt}
                                                        loading={index === 0 ? 'eager' : 'lazy'}
                                                        fetchPriority={index === 0 ? 'high' : 'auto'}
                                                        decoding="async"
                                                        className="h-full w-full object-cover object-center"
                                                    />
                                                </picture>
                                            </div>
                                        )}
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                    </div>
                </section>

                {/* Search Section - overlays hero */}
                <section className="relative z-10 -mt-12 md:-mt-48">
                    <div className="mx-6 md:mx-36 xl:mx-80">
                        <div className="rounded-lg bg-white p-6 shadow-lg">
                            {/* CN-200 (5.4): tabs "Embarcações | Empresas" + "Quero vender" (same destination as today) */}
                            <div className="mb-6 flex items-end justify-between gap-4">
                                <SearchTypeTabs value={homeSearchType} onChange={handleHomeSearchTypeChange} idPrefix="home-search" className="border-b-0" />
                                <Link href="/vender" className="shrink-0 cursor-pointer px-1 pb-3 text-gray-600 hover:text-blue-800">
                                    Quero vender
                                </Link>
                            </div>

                            {homeSearchType === 'empresas' ? (
                                <div id="home-search-panel-empresas" role="tabpanel" aria-labelledby="home-search-tab-empresas">
                                    <SmartSearchForm
                                        type="empresas"
                                        origin="home"
                                        placeholder="Que serviço ou empresa você procura?"
                                        showExamples
                                        showShortcuts
                                        onSwitchType={handleHomeSearchTypeChange}
                                    />
                                </div>
                            ) : (
                            <div id="home-search-panel-embarcacoes" role="tabpanel" aria-labelledby="home-search-tab-embarcacoes">
                            {/* Sub Pills */}
                            <div className="mb-6 flex justify-center gap-2 lg:justify-start">
                                <Button size="sm" onClick={() => setActiveCondition('all')} className={getConditionPillClass('all')}>
                                    Todos
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setActiveCondition('new')} className={getConditionPillClass('new')}>
                                    Novos
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setActiveCondition('used')}
                                    className={getConditionPillClass('used')}
                                >
                                    Usados
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => router.visit('/cotas-nauticas')}
                                    className="rounded-full bg-blue-50 px-4 py-1 text-sm text-blue-800 hover:bg-gray-200 hover:text-gray-700"
                                >
                                    Cotas
                                </Button>
                            </div>

                            {boatsSmartSearchEnabled ? (
                                /* CN-200 (5.4): smart search field, example phrases and "Prefiro escolher nos filtros" */
                                <div className="mb-2">
                                    <SmartSearchForm
                                        type="embarcacoes"
                                        origin="home"
                                        placeholder="Que embarcação você procura? Ex.: lancha para pernoite até 300 mil em SC"
                                        showExamples
                                        showPreferFilters
                                        extraParams={activeCondition !== 'all' ? { condition: activeCondition } : {}}
                                        onPreferFilters={() => router.visit(`${search().url}?type=embarcacoes&filters=1${activeCondition !== 'all' ? `&condition=${activeCondition}` : ''}`)}
                                        onSwitchType={handleHomeSearchTypeChange}
                                    />
                                </div>
                            ) : (
                                <>
                                    {/* Search Input with Autocomplete (kept while the smart search of boats is off in the admin) */}
                                    <div className="mb-4">
                                        <SearchAutocomplete
                                            placeholder="Busque por marca, modelo ou local"
                                            initialValue={mainSearchQuery}
                                            onChange={(query) => {
                                                setMainSearchQuery(query);
                                            }}
                                            onSearch={(query) => {
                                                setMainSearchQuery(query);
                                                const params: Record<string, string> = { pesquisa: query };
                                                if (activeCondition !== 'all') {
                                                    params.condition = activeCondition;
                                                }
                                                router.get(search().url, params);
                                            }}
                                        />
                                    </div>

                                    {/* Filter Dropdown */}
                                    <button
                                        className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                                        onClick={() => setIsAdvancedSearchOpen((current) => !current)}
                                        aria-expanded={isAdvancedSearchOpen}
                                        aria-controls="advancedSearchSection"
                                        type="button"
                                    >
                                        <ChevronDown className={cn('mr-1 h-4 w-4 transition-transform', isAdvancedSearchOpen && 'rotate-180')} />
                                        {isAdvancedSearchOpen ? 'Recolher filtro' : 'Mais opções de filtro'}
                                    </button>
                                </>
                            )}
                            </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Advanced Search Section (only while the smart search of boats is off) */}
                <section id="advancedSearchSection" className={cn('py-5', isAdvancedSearchOpen && !boatsSmartSearchEnabled ? 'block' : 'hidden')}>
                    <div className="mx-6 lg:mx-24">
                        <div className="rounded-lg bg-white p-6 shadow-lg">
                            {/* Header */}
                            <h3 className="mb-4 text-lg font-medium text-gray-900">Busca Avançada</h3>

                            {/* Search Input and Button */}
                            {mainSearchQuery && <p className="mb-2 text-xs text-blue-600">Será combinado com: "{mainSearchQuery}"</p>}
                            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                                <div>
                                    <label htmlFor="home-min-price" className="mb-2 block text-sm text-gray-600">
                                        Valor Mínimo
                                    </label>
                                    <Input
                                        id="home-min-price"
                                        placeholder="R$ 0,00"
                                        className="h-12 w-full rounded-lg border border-gray-300 px-4"
                                        value={minPrice}
                                        onChange={handleMinPriceChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAdvancedSearch();
                                            }
                                        }}
                                        inputMode="numeric"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="home-max-price" className="mb-2 block text-sm text-gray-600">
                                        Valor Máximo
                                    </label>
                                    <Input
                                        id="home-max-price"
                                        placeholder="R$ 0,00"
                                        className="h-12 w-full rounded-lg border border-gray-300 px-4"
                                        value={maxPrice}
                                        onChange={handlePriceChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAdvancedSearch();
                                            }
                                        }}
                                        inputMode="numeric"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="home-size" className="mb-2 block text-sm text-gray-600">
                                        Tamanho
                                    </label>
                                    <Input
                                        id="home-size"
                                        placeholder="0 pés"
                                        className="h-12 w-full rounded-lg border border-gray-300 px-4"
                                        value={size}
                                        onChange={handleSizeChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleAdvancedSearch();
                                            }
                                        }}
                                        inputMode="decimal"
                                        type="text"
                                    />
                                </div>
                            </div>

                            {advancedSearchError && <p className="mb-3 text-sm text-red-600">{advancedSearchError}</p>}

                            <div className="mt-4 flex justify-center">
                                <Button
                                    onClick={handleAdvancedSearch}
                                    className="h-12 rounded-lg bg-blue-800 px-8 text-xl font-medium text-white hover:bg-blue-800"
                                >
                                    Buscar
                                    <Search className="mr-2 h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Solutions Section */}
                <section className="py-16">
                    <div className="mx-6 lg:mx-24">
                        <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal text-gray-900">Soluções</h2>

                        {/* Mobile Layout - Icons and titles only */}
                        <div className="flex justify-between md:hidden">
                            <Link href="/servicos" className="flex flex-col items-center">
                                <div className="mb-3 flex h-[77px] w-[77px] items-center justify-center rounded-full bg-primary p-3">
                                    <img src={`/servicos.svg`} alt="Serviços" width={38} height={38} className="object-contain" />
                                </div>
                                <h3 className="text-center text-[15px] leading-[19px] font-normal text-black">Serviços</h3>
                            </Link>

                            <Link href="/vender" className="flex flex-col items-center">
                                <div className="mb-3 flex h-[77px] w-[77px] items-center justify-center rounded-full bg-primary p-3">
                                    <img src={`/vender.svg`} alt="Vender" width={38} height={38} className="object-contain" />
                                </div>
                                <h3 className="text-center text-[15px] leading-[19px] font-normal text-black">Vender</h3>
                            </Link>

                            <a href="https://cursos.comprenautica.com.br" target="_blank" rel="noopener noreferrer">
                                <div className="flex flex-col items-center">
                                    <div className="mb-3 flex h-[77px] w-[77px] items-center justify-center rounded-full bg-primary p-3">
                                        <img src={`/conhecimento.svg`} alt="Conhecimento" width={38} height={38} className="object-contain" />
                                    </div>
                                    <h3 className="text-center text-[15px] leading-[19px] font-normal text-black">Conhecimento</h3>
                                </div>
                            </a>
                        </div>

                        {/* Desktop Layout - Full cards with descriptions */}
                        <div className="hidden gap-8 md:grid md:grid-cols-3">
                            <Link href="/servicos" className="block h-full">
                                <Card className="h-full cursor-pointer border-0 bg-white p-6 shadow-md transition-shadow hover:shadow-lg">
                                    <CardContent className="flex h-full flex-col p-0">
                                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-800 p-3">
                                            <img
                                                src={`/servicos.svg`}
                                                alt={`servicos.svg`.replace('\n', ' ')}
                                                width={80}
                                                height={80}
                                                className="w-4/5 object-contain"
                                            />
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-gray-900">Empresas e serviços</h3>
                                        <p className="text-sm leading-relaxed text-gray-600">
                                            O Catálogo Náutico da CN funciona como um guia de empresas e serviços do setor náutico, permitindo que
                                            qualquer pessoa encontre, de forma rápida e descomplicada, os profissionais e soluções que precisa para
                                            sua embarcação.
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>

                            <Link href="/vender" className="block h-full">
                                <Card className="h-full cursor-pointer border-0 bg-white p-6 shadow-md transition-shadow hover:shadow-lg">
                                    <CardContent className="flex h-full flex-col p-0">
                                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-800 p-3">
                                            <img
                                                src={`/vender.svg`}
                                                alt={`vender.svg`.replace('\n', ' ')}
                                                width={80}
                                                height={80}
                                                className="w-4/5 object-contain"
                                            />
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-gray-900">Vender</h3>
                                        <p className="text-sm leading-relaxed text-gray-600">
                                            Na CN, você anuncia sua embarcação e fala direto com quem quer comprar. A negociação acontece entre as
                                            partes, enquanto a plataforma oferece visibilidade, organização e facilidade para conectar pessoas.
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>

                            <a href="https://cursos.comprenautica.com.br" target="_blank" rel="noopener noreferrer" className="block h-full">
                                <Card className="h-full cursor-pointer border-0 bg-white p-6 shadow-md transition-shadow hover:shadow-lg">
                                    <CardContent className="flex h-full flex-col p-0">
                                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-800 p-3">
                                            <img
                                                src={`/conhecimento.svg`}
                                                alt={`conhecimento.svg`.replace('\n', ' ')}
                                                width={80}
                                                height={80}
                                                className="w-4/5 object-contain"
                                            />
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-gray-900">Conhecimento</h3>
                                        <p className="text-sm leading-relaxed text-gray-600">
                                            Na CN você encontra cursos e conteúdos náuticos de vários produtores, com diferentes formatos e temas,
                                            pensados para atender desde iniciantes até quem já vive a náutica. O link dessa parte é
                                            cursos.comprenautica.com.br
                                        </p>
                                    </CardContent>
                                </Card>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Categories Section */}
                <section className="pb-16">
                    <div className="mx-6 lg:mx-24">
                        <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal text-gray-900">Categorias</h2>

                        {/* Mobile Layout - Horizontal Carousel */}
                        <div className="md:hidden">
                            <div className="h-[161px] w-full overflow-x-auto overflow-y-hidden">
                                <div className="flex h-[161px] w-[777px] bg-background">
                                    {categories?.map((category) => (
                                        <Link
                                            key={category.id}
                                            href={`/${category.slug}`}
                                            className="group relative ml-5 flex h-[141px] w-[158px] cursor-pointer flex-col items-center justify-end rounded-lg bg-[#D9D9D9] first:ml-0"
                                        >
                                            {/* Image Container */}
                                            <div className="absolute inset-0 overflow-hidden rounded-lg">
                                                <img
                                                    src={getMediaUrl(category.logo, 'card', '/jetski-1.jpg')}
                                                    alt={category.name}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>

                                            {/* Gradient Overlay */}
                                            <div className="absolute right-0 bottom-0 left-0 h-[70%] rounded-b-lg bg-gradient-to-t from-primary from-[8.17%] to-[rgba(115,115,115,0)] to-[70.19%]" />

                                            {/* Category Title */}
                                            <div className="relative z-10 mb-4 px-2">
                                                <h3 className="text-center text-[20px] leading-[25px] font-medium text-white uppercase">
                                                    {category.name}
                                                </h3>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Desktop Layout - Grid */}
                        <div className="hidden gap-6 md:grid md:grid-cols-4">
                            {categories?.map((category) => (
                                <Link
                                    key={category.id}
                                    href={`/${category.slug}`}
                                    className="group relative cursor-pointer overflow-hidden rounded-xl"
                                >
                                    <div className="relative aspect-[4/3]">
                                        <img
                                            src={getMediaUrl(category.logo, 'card', '/jetski-1.jpg')}
                                            alt={category.name}
                                            loading="lazy"
                                            decoding="async"
                                            className="h-full w-full object-cover"
                                        />
                                        <div className="absolute right-0 bottom-0 left-0 h-1/3 bg-gradient-to-t from-blue-800/100 via-blue-800/80 to-transparent" />
                                        <div className="absolute bottom-4 left-4">
                                            <h3 className="text-base font-bold text-white group-hover:hidden">{category.name.toUpperCase()}</h3>
                                            <h3 className="hidden w-[80%] text-base font-bold text-white group-hover:block">
                                                VEJA {category.name.toUpperCase()} DISPONÍVEIS
                                            </h3>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Featured Listings Section */}
                <section className="pb-16">
                    <div className="mx-6 lg:mx-24">
                        <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal">Destaques</h2>

                        {featured && featured.length > 0 ? (
                            <ClassifiedsInlineBannerCarousel
                                classifieds={featured}
                                banner={featuredBanner}
                                favoriteStates={favoriteStates}
                                onFavorite={handleFavorite}
                                onBannerClick={handleBannerClick}
                                onBannerVisible={trackBannerView}
                                getPriceLabel={(classified) =>
                                    getPublicPriceLabel({
                                        price: classified.price,
                                        priceOnRequest: classified.price_on_request,
                                    })
                                }
                                getConditionLabel={(classified) => (classified.condition === 'new' ? 'Novo' : 'Usado')}
                                layoutMode="columns"
                                bannerPlacement="welcome-featured"
                                mobileCardVariant="desktop"
                            />
                        ) : (
                            <div className="flex h-[198px] w-full items-center justify-center">
                                <p className="text-center text-gray-600">Nenhum anúncio em destaque no momento.</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* Most Searched Section */}
                <MostSearchedCategories
                    fallbackCategories={[
                        { name: 'REAL POWERBOATS-320', svg: '/real-powerboats-320.svg' },
                        { name: 'YAMAHA-VX SPORT', svg: '/yamaha-vx-sport.svg' },
                        { name: 'REAL POWERBOATS', svg: '/real-powerboats.svg' },
                        { name: 'ECOMARINER ALFA', svg: '/ecomariner-alfa.svg' },
                        { name: 'ECOMARINER ALFANTT 3 JOXT2 RECREATIVO', svg: '/ecomariner-alfa.svg' },
                    ]}
                />

                {/* Recent Views Section */}
                {recentlyViewed && recentlyViewed.length > 0 && (
                    <section className="pb-16">
                        <div className="mx-6 lg:mx-24">
                            <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal">Seus últimos vistos</h2>

                            <ClassifiedsInlineBannerCarousel
                                classifieds={recentlyViewed}
                                banner={recentlyViewedBanner}
                                favoriteStates={favoriteStates}
                                onFavorite={handleFavorite}
                                onBannerClick={handleBannerClick}
                                onBannerVisible={trackBannerView}
                                getPriceLabel={(classified) =>
                                    getPublicPriceLabel({
                                        price: classified.price,
                                        priceOnRequest: classified.price_on_request,
                                    })
                                }
                                getConditionLabel={(classified) => (classified.condition === 'new' ? 'Novo' : 'Usado')}
                                bannerPlacement="welcome-recently-viewed"
                                layoutMode="columns"
                            />
                        </div>
                    </section>
                )}

                {/* Sponsor Banner */}
                {sponsorBanner && (
                    <section>
                        <div ref={sponsorBannerRef} className="mx-6 flex justify-center lg:mx-24">
                            {sponsorBanner.link_url ? (
                                <a
                                    href={sponsorBanner.link_url}
                                    onClick={(e) => handleBannerClick(e, sponsorBanner.id, sponsorBanner.link_url)}
                                    className="flex h-[210px] w-full cursor-pointer items-center justify-center bg-gray-200 md:aspect-[97/25] md:h-auto"
                                    aria-label={sponsorBanner.image_alt || sponsorBanner.title}
                                >
                                    {sponsorImageSrc && (
                                        <picture className="h-full w-full">
                                            {sponsorMobileImage && <source media="(max-width: 767px)" srcSet={sponsorMobileImage} />}
                                            <img
                                                src={sponsorImageSrc}
                                                alt={sponsorBanner.image_alt || sponsorBanner.title}
                                                loading="lazy"
                                                decoding="async"
                                                className="h-full w-full object-contain object-center md:object-cover"
                                            />
                                        </picture>
                                    )}
                                </a>
                            ) : (
                                <div className="flex h-[210px] w-full items-center justify-center md:aspect-[97/25] md:h-auto">
                                    {sponsorImageSrc && (
                                        <picture className="h-full w-full">
                                            {sponsorMobileImage && <source media="(max-width: 767px)" srcSet={sponsorMobileImage} />}
                                            <img
                                                src={sponsorImageSrc}
                                                alt={sponsorBanner.image_alt || sponsorBanner.title}
                                                loading="lazy"
                                                decoding="async"
                                                className="h-full w-full object-cover object-center"
                                            />
                                        </picture>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* News Section */}
                <section className="h-auto py-12 md:py-16">
                    <div className="mx-6 lg:mx-24">
                        <div className="mb-6 flex items-end justify-between gap-4 md:mb-8">
                            <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Notícias</h2>
                            <p className="hidden max-w-md text-sm leading-relaxed text-slate-500 md:block">
                                Acompanhe lançamentos, eventos e tendências do mercado náutico.
                            </p>
                        </div>
                        <Carousel
                            opts={{
                                align: 'start',
                                loop: false,
                                containScroll: 'trimSnaps',
                            }}
                            className="h-full w-full"
                        >
                            <CarouselContent className="-ml-4 xl:-ml-6" containerClassName="h-full">
                                {blogPosts.map((item) => {
                                    if (!item.autor || item.autor == null) {
                                        item.autor = {
                                            id: 2,
                                            nome: 'Carolina Piazentin',
                                        };
                                    }
                                    if (!item.categoria || item.categoria == null) {
                                        item.categoria = {
                                            id: 4129,
                                            nome: 'Notícias',
                                            slug: 'noticias',
                                        };
                                    }

                                    return (
                                        <CarouselItem key={item.id} className="basis-full pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 xl:pl-6">
                                            <a href={item.url} className="block h-full">
                                                <article className="flex h-full min-h-[420px] flex-col overflow-hidden rounded-xl border bg-white shadow-sm">
                                                    <div className="relative aspect-3/2 overflow-hidden bg-slate-100">
                                                        <img
                                                            src={item.imagem}
                                                            alt={item.titulo}
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="h-full w-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex flex-1 flex-col gap-3 p-5 xl:gap-4 xl:p-6">
                                                        <p className="text-sm font-medium text-slate-500">{item.categoria.nome}</p>
                                                        <h3 className="line-clamp-2 min-h-[2lh] text-lg leading-snug font-semibold text-slate-900">
                                                            {item.titulo}
                                                        </h3>
                                                        <p className="line-clamp-3 text-sm leading-relaxed text-slate-500">{item.resumo}</p>
                                                        <p className="mt-auto text-sm text-slate-500">{'Por ' + item.autor.nome}</p>
                                                    </div>
                                                </article>
                                            </a>
                                        </CarouselItem>
                                    );
                                })}

                                {newsBanner && (
                                    <CarouselItem className="basis-full pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 xl:pl-6">
                                        <div ref={newsBannerRef} className="h-full">
                                            <Card className="flex h-full min-h-[420px] items-center justify-center overflow-hidden rounded-xl border-0 bg-primary p-0 shadow-sm">
                                                {newsBanner.link_url ? (
                                                    <a
                                                        href={newsBanner.link_url}
                                                        onClick={(e) => handleBannerClick(e, newsBanner.id, newsBanner.link_url)}
                                                        className="flex h-full w-full cursor-pointer items-center justify-center"
                                                        aria-label={newsBanner.image_alt || newsBanner.title}
                                                    >
                                                        <img
                                                            src={newsBannerImage}
                                                            alt={newsBanner.image_alt || newsBanner.title || 'Advertisement'}
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="h-full w-full object-cover object-center"
                                                        />
                                                    </a>
                                                ) : (
                                                    <img
                                                        src={newsBannerImage}
                                                        alt={newsBanner.image_alt || newsBanner.title || 'Advertisement'}
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="h-full w-full object-cover object-center"
                                                    />
                                                )}
                                            </Card>
                                        </div>
                                    </CarouselItem>
                                )}
                            </CarouselContent>

                            <div className="mt-6 flex items-center justify-end gap-3 pr-1">
                                <CarouselPrevious className="static h-10 w-10 translate-y-0 border border-slate-300 bg-white hover:bg-slate-50" />
                                <CarouselNext className="static h-10 w-10 translate-y-0 border border-slate-300 bg-white hover:bg-slate-50" />
                            </div>
                        </Carousel>
                    </div>
                </section>

                {/* Newsletter Section - Desktop */}
                <section className="relative hidden h-[80vh] overflow-hidden bg-blue-800 pb-16 text-center text-white md:block">
                    <div className="absolute inset-0">
                        <img
                            src="/boat-7-newsletter.jpg"
                            alt="Newsletter background"
                            loading="lazy"
                            fetchPriority="low"
                            decoding="async"
                            className="h-full w-full object-cover object-bottom"
                        />
                        <div className="absolute inset-0 bg-blue-800/70"></div>
                    </div>
                    <div className="relative mx-6 flex h-full flex-col justify-center lg:mx-24">
                        <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal">Faça parte da lista</h2>
                        <p className="mx-auto mb-8 max-w-2xl text-lg">Fique por dentro de tudo o que acontece no mundo náutico.</p>
                        {emailSent ? (
                            <div className="mx-auto mb-4 max-w-2xl">
                                <div className="flex items-center justify-center gap-3 rounded-full bg-blue-900 px-6 py-4 text-center text-white">
                                    <p className="text-lg">
                                        Email Enviado! Participe da nossa comunidade de WhatsApp{' '}
                                        <a
                                            href="https://chat.whatsapp.com/DDV5CasTDCGGVPbCBzgubw?mode=wwt"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 font-semibold underline hover:text-blue-200"
                                        >
                                            clicando aqui
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 47 47"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M40.1523 6.83013C35.7341 2.42802 29.8582 0.00247427 23.5981 0C10.6994 0 0.201381 10.447 0.196219 23.2881C0.194498 27.3928 1.27214 31.3996 3.31992 34.9313L0 47L12.4057 43.7614C15.8237 45.6168 19.6721 46.5945 23.5888 46.5962H23.5984C36.4958 46.5962 46.9948 36.1478 47 23.3067C47.0025 17.0837 44.5706 11.2322 40.1523 6.83013ZM23.5984 42.6627H23.5904C20.1003 42.6613 16.677 41.7282 13.6907 39.9645L12.9804 39.545L5.61878 41.4668L7.58377 34.3237L7.12124 33.5914C5.17421 30.5093 4.14583 26.947 4.14736 23.2896C4.15163 12.6166 12.8772 3.93346 23.6061 3.93346C28.8013 3.93542 33.685 5.95158 37.3573 9.61045C41.0296 13.2693 43.0509 18.1329 43.0489 23.3052C43.0445 33.9789 34.319 42.6627 23.5984 42.6627ZM34.2674 28.1651C33.6827 27.8738 30.8079 26.466 30.2719 26.2718C29.7359 26.0776 29.3462 25.9805 28.9563 26.5631C28.5665 27.1456 27.4459 28.4564 27.1047 28.8447C26.7637 29.2331 26.4226 29.2817 25.8379 28.9904C25.2532 28.6991 23.3691 28.0847 21.1356 26.1021C19.3974 24.5592 18.2238 22.6534 17.8827 22.0708C17.5417 21.4882 17.8465 21.1732 18.1392 20.8832C18.4022 20.6224 18.7239 20.2035 19.0163 19.8636C19.3086 19.5238 19.406 19.2811 19.6009 18.8928C19.7959 18.5044 19.6984 18.1645 19.5522 17.8733C19.406 17.582 18.2367 14.7177 17.7494 13.5525C17.2748 12.4178 16.7927 12.5713 16.4338 12.5536C16.0931 12.5367 15.7029 12.5331 15.3131 12.5331C14.9233 12.5331 14.2898 12.6787 13.7538 13.2613C13.2179 13.8439 11.7074 15.2517 11.7074 18.116C11.7074 20.9802 13.8026 23.7474 14.0949 24.1358C14.3873 24.5242 18.2181 30.4021 24.0837 32.9227C25.4788 33.5223 26.568 33.8803 27.4172 34.1486C28.818 34.5914 30.0927 34.5289 31.1002 34.3791C32.2236 34.2121 34.5597 32.9713 35.047 31.612C35.5343 30.2527 35.5343 29.0875 35.3881 28.8448C35.242 28.6021 34.8521 28.4564 34.2674 28.1651Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </a>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <form onSubmit={handleNewsletterSubmit} className="mx-auto mb-4 w-3xl">
                                    <div className="relative flex">
                                        <Input
                                            type="email"
                                            placeholder="Insira seu email aqui"
                                            className="h-15 rounded-full bg-white pr-40 pl-8 text-black"
                                            value={newsletterEmail}
                                            onChange={(e) => setNewsletterEmail(e.target.value)}
                                            required
                                            disabled={newsletterLoading}
                                        />
                                        <Button
                                            type="submit"
                                            className="absolute top-1/2 right-0 h-15 -translate-y-1/2 rounded-full bg-blue-900 px-12 hover:bg-blue-950"
                                            disabled={newsletterLoading}
                                        >
                                            {newsletterLoading ? 'Enviando...' : 'Enviar'}
                                        </Button>
                                    </div>
                                </form>
                                {newsletterMessage && (
                                    <p className={`mb-8 text-center ${newsletterStatus === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                        {newsletterMessage}
                                    </p>
                                )}
                            </>
                        )}

                        {/* Social Media Icons */}
                        <h2 className="mb-6 align-middle text-2xl leading-none font-bold tracking-normal">Nossas Redes</h2>
                        <div className="flex justify-center gap-4">
                            <a
                                href="https://www.linkedin.com/company/comprenautica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn da Compre Náutica"
                            >
                                <Button variant="ghost" size="sm" className="h-12 w-12 rounded-full bg-white/20 p-0 hover:bg-white/30">
                                    <Linkedin className="h-5 w-5" />
                                </Button>
                            </a>
                            <a
                                href="https://www.facebook.com/comprenautica/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook da Compre Náutica"
                            >
                                <Button variant="ghost" size="sm" className="h-12 w-12 rounded-full bg-white/20 p-0 hover:bg-white/30">
                                    <Facebook className="h-5 w-5" />
                                </Button>
                            </a>
                            <a
                                href="https://twitter.com/CompreNautica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Twitter da Compre Náutica"
                            >
                                <Button variant="ghost" size="sm" className="h-12 w-12 rounded-full bg-white/20 p-0 hover:bg-white/30">
                                    <Twitter className="h-5 w-5" />
                                </Button>
                            </a>
                            <a
                                href="https://www.instagram.com/comprenautica/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram da Compre Náutica"
                            >
                                <Button variant="ghost" size="sm" className="h-12 w-12 rounded-full bg-white/20 p-0 hover:bg-white/30">
                                    <Instagram className="h-5 w-5" />
                                </Button>
                            </a>
                            <a
                                href="https://www.youtube.com/c/CompreN%C3%A1utica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="YouTube da Compre Náutica"
                            >
                                <Button variant="ghost" size="sm" className="h-12 w-12 rounded-full bg-white/20 p-0 hover:bg-white/30">
                                    <Youtube className="h-5 w-5" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Mobile Newsletter Section */}
                <section className="relative h-[334px] overflow-hidden bg-[#D9D9D9] text-center text-white md:hidden">
                    <div className="absolute inset-0">
                        <img
                            src="/boat-7-newsletter-mobile.jpg"
                            alt="Newsletter background"
                            loading="lazy"
                            fetchPriority="low"
                            decoding="async"
                            className="h-full w-full object-cover object-bottom"
                        />
                        <div
                            className="absolute inset-0"
                            style={{
                                background: 'linear-gradient(270deg, #005BA4 0%, rgba(0, 91, 166, 0.37) 100%)',
                                transform: 'rotate(-180deg)',
                            }}
                        ></div>
                    </div>
                    <div className="relative flex h-full flex-col justify-center px-6">
                        {/* Title */}
                        <h2 className="mb-6 text-[15px] leading-[19px] font-bold text-[#FAFAFA]">Faça parte da lista</h2>

                        {/* Form */}
                        {emailSent ? (
                            <div className="mx-auto mb-6 max-w-[321px]">
                                <div className="flex items-center justify-center gap-2 rounded-[30px] bg-primary px-4 py-3 text-center">
                                    <p className="text-[14px] leading-[18px] font-normal text-[#FAFAFA]">
                                        Email Enviado! Participe da nossa comunidade de WhatsApp{' '}
                                        <a
                                            href="https://chat.whatsapp.com/DDV5CasTDCGGVPbCBzgubw?mode=wwt"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 font-semibold underline hover:text-blue-200"
                                        >
                                            clicando aqui
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 47 47"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M40.1523 6.83013C35.7341 2.42802 29.8582 0.00247427 23.5981 0C10.6994 0 0.201381 10.447 0.196219 23.2881C0.194498 27.3928 1.27214 31.3996 3.31992 34.9313L0 47L12.4057 43.7614C15.8237 45.6168 19.6721 46.5945 23.5888 46.5962H23.5984C36.4958 46.5962 46.9948 36.1478 47 23.3067C47.0025 17.0837 44.5706 11.2322 40.1523 6.83013ZM23.5984 42.6627H23.5904C20.1003 42.6613 16.677 41.7282 13.6907 39.9645L12.9804 39.545L5.61878 41.4668L7.58377 34.3237L7.12124 33.5914C5.17421 30.5093 4.14583 26.947 4.14736 23.2896C4.15163 12.6166 12.8772 3.93346 23.6061 3.93346C28.8013 3.93542 33.685 5.95158 37.3573 9.61045C41.0296 13.2693 43.0509 18.1329 43.0489 23.3052C43.0445 33.9789 34.319 42.6627 23.5984 42.6627ZM34.2674 28.1651C33.6827 27.8738 30.8079 26.466 30.2719 26.2718C29.7359 26.0776 29.3462 25.9805 28.9563 26.5631C28.5665 27.1456 27.4459 28.4564 27.1047 28.8447C26.7637 29.2331 26.4226 29.2817 25.8379 28.9904C25.2532 28.6991 23.3691 28.0847 21.1356 26.1021C19.3974 24.5592 18.2238 22.6534 17.8827 22.0708C17.5417 21.4882 17.8465 21.1732 18.1392 20.8832C18.4022 20.6224 18.7239 20.2035 19.0163 19.8636C19.3086 19.5238 19.406 19.2811 19.6009 18.8928C19.7959 18.5044 19.6984 18.1645 19.5522 17.8733C19.406 17.582 18.2367 14.7177 17.7494 13.5525C17.2748 12.4178 16.7927 12.5713 16.4338 12.5536C16.0931 12.5367 15.7029 12.5331 15.3131 12.5331C14.9233 12.5331 14.2898 12.6787 13.7538 13.2613C13.2179 13.8439 11.7074 15.2517 11.7074 18.116C11.7074 20.9802 13.8026 23.7474 14.0949 24.1358C14.3873 24.5242 18.2181 30.4021 24.0837 32.9227C25.4788 33.5223 26.568 33.8803 27.4172 34.1486C28.818 34.5914 30.0927 34.5289 31.1002 34.3791C32.2236 34.2121 34.5597 32.9713 35.047 31.612C35.5343 30.2527 35.5343 29.0875 35.3881 28.8448C35.242 28.6021 34.8521 28.4564 34.2674 28.1651Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </a>
                                        !
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <form onSubmit={handleNewsletterSubmit} className="mx-auto mb-6 w-[321px]">
                                    <div className="relative flex h-[55px]">
                                        <input
                                            type="email"
                                            placeholder="Insira seu email aqui"
                                            className="h-full flex-1 rounded-[30px] border border-[#646A74] bg-[#FAFAFA] px-4 text-[15px] leading-[19px] font-normal text-black placeholder:text-gray-600"
                                            value={newsletterEmail}
                                            onChange={(e) => setNewsletterEmail(e.target.value)}
                                            required
                                            disabled={newsletterLoading}
                                        />
                                        <button
                                            type="submit"
                                            className="absolute top-1/2 right-0 h-full w-[120px] -translate-y-1/2 rounded-[30px] bg-primary text-[15px] leading-[19px] font-normal text-[#FAFAFA]"
                                            disabled={newsletterLoading}
                                        >
                                            {newsletterLoading ? 'Enviando...' : 'Enviar'}
                                        </button>
                                    </div>
                                </form>

                                {newsletterMessage && (
                                    <p
                                        className={`mb-4 text-center text-[12px] ${newsletterStatus === 'success' ? 'text-green-400' : 'text-red-400'}`}
                                    >
                                        {newsletterMessage}
                                    </p>
                                )}
                            </>
                        )}

                        {/* Social Media Title */}
                        <h3 className="mb-4 text-[15px] leading-[19px] font-bold text-[#FAFAFA]">Nossas Redes</h3>

                        {/* Social Media Icons */}
                        <div className="flex justify-center gap-3">
                            <a
                                href="https://www.linkedin.com/company/comprenautica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn da Compre Náutica"
                                className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-primary p-0"
                            >
                                <Linkedin className="h-5 w-5 text-[#FAFAFA]" />
                            </a>
                            <a
                                href="https://www.facebook.com/comprenautica/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook da Compre Náutica"
                                className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-primary p-0"
                            >
                                <Facebook className="h-5 w-5 text-[#FAFAFA]" />
                            </a>
                            <a
                                href="https://twitter.com/CompreNautica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Twitter da Compre Náutica"
                                className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-primary p-0"
                            >
                                <Twitter className="h-5 w-5 text-[#FAFAFA]" />
                            </a>
                            <a
                                href="https://www.instagram.com/comprenautica/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram da Compre Náutica"
                                className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-primary p-0"
                            >
                                <Instagram className="h-5 w-5 text-[#FAFAFA]" />
                            </a>
                            <a
                                href="https://www.youtube.com/c/CompreN%C3%A1utica"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="YouTube da Compre Náutica"
                                className="flex h-[47px] w-[47px] items-center justify-center rounded-full bg-primary p-0"
                            >
                                <Youtube className="h-5 w-5 text-[#FAFAFA]" />
                            </a>
                        </div>
                    </div>
                </section>
            </div>
        </FrontendLayout>
    );
}

export default function Welcome() {
    return (
        <RecaptchaProvider>
            <WelcomeContent />
        </RecaptchaProvider>
    );
}
